import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, BookOpen, Settings, List, Home } from 'lucide-react';

// 分页函数 - 根据内容长度自动分页
const splitContentIntoPages = (content: string, maxLinesPerPage: number = 20) => {
  const lines = content.split('\n');
  const pages = [];
  
  for (let i = 0; i < lines.length; i += maxLinesPerPage) {
    const pageLines = lines.slice(i, i + maxLinesPerPage);
    pages.push(pageLines.join('\n'));
  }
  
  return pages;
};

// 模拟书籍数据
const bookData = {
  title: "山止川行",
  author: "淇",
  pages: [
    {
      id: 1,
      title: "第一章",
      content: `我们仰山，山仰我，我心有山。
山山而川，泛泛而舟。
山止川行，不过而而。`,
      pageNumber: 18,
      isChapter: true
    },
    // 高大善人(狂人自序) - 分成多页
    ...splitContentIntoPages(`在下高子淇，君子之子；瞻彼淇奥，绿竹如箦之淇。

岁在甲申 正阳之月 北方一隅 懵懂出生
瞻彼淇奥 绿竹猗猗 有匪君子 如切如磋
故名子淇 随父之姓 望如小溪 涓涓细流
幼时安度 琴耽瑟好 三世同堂 心存感激
昔年挚交 今寻无踪 遍插茱萸 少我一人
幸有贤弟 幼吾五岁 温润如玉 笙磬同音 
运交驿马 北上盛京 总角之时 孑然一身
自命不凡 壮志凌云 恃才傲物 目中无人
哂于人际 潜神于学 小有成就 自恃甚高
未至及笄 赠与刀锋 愧负重任 疚于托付
弗敢直面 业荒于嬉 今觉荒谬 何苦一提
又欲离家 亦为求学 前程未卜 胜败难分
纵有万艰 亦不会悔 痛醒之后 继续远走
初心不改 矢志不渝 如月之恒 如日之升
常有一梦 吾乃侠客 纵马长歌 把酒仗剑
自携泰山 以超北海 天人合一 山河同颂
梦终是梦 身乃是客 此番梦醒 问今何年
何以为乐 于世俗处 一二好友 诵诗踏歌
何以为哀 万贯家财 身前有名 仅此而已 
何以为求 高大善人 翩翩公子 景行含光 
何以为贵 身无他物 一肩明月 两袖清风
不谴是非 痴者自云 大道无用 风月刻诵
浮生一日 蜉蝣一世 千秋之后 谁能不朽
愿君能作清明客 借路腥风不染尘

2021年4月21日执笔`, 15).map((pageContent, index) => ({
      id: 2 + index,
      title: "高大善人(狂人自序)",
      content: pageContent,
      pageNumber: 2 + index,
      isMultiPage: true,
      isChapter: false
    })),
    
    // 侠士歌
    {
      id: 4,
      title: "侠士歌",
      content: `听闻东海境，渺浵一青洲。
四时花盈树，豪侠任纵游。
中有碧玉女，飒飒动林丘。
随枝倾而动，一剑舞风流。
昔日酒家饮，醉意解千愁。
今朝飘零叶，散发弄扁舟。
功名非本志，金钱意气丢。
欲行何所处，天涯去悠悠。`,
      pageNumber: 4,
      isChapter: false
    },
    
    // 一梦江湖
    {
      id: 5,
      title: "一梦江湖",
      content: "记，与其相濡以沫，不如相忘于江湖",
      pageNumber: 5,
      isChapter: true
    },
    ...splitContentIntoPages(`记，与其相濡以沫，不如相忘于江湖

我早已离开江湖，
心中只剩下濡沫。

偶尔会想起那雪山下的金戈铁马，
云梦泽的藤萝松柏，
暗香的扶疏枝影，
华山的纵横剑气。

忽忆得，
也曾银鞍配马，
换得吴姬压酒，
也曾上落日楼头，
望那六朝流水。

想当年，
乘着梁武鸡鸣，
章台绿柳，
酌一杯江月，
漫品烟火。

士人风骨，
书生意气，
任侠狂痴。

想那江南烟雨，
想那杏花满路，
至今回响在青石板上的
一声声余韵，
也许都是我的蛩音。

想那黄河落日
塞北风雪，
想那…
！

春风一杯酒，
江湖十年梦。
叹而今，
离江湖也远矣，
心中却还藏着它。

先生长劝我忘却
言：
安时而处顺，哀乐不能入。
可即使要忘却，
也要相忘于江湖呐！

或许，大概，
我终会回来。`, 15).map((pageContent, index) => ({
      id: 6 + index,
      title: "一梦江湖",
      content: pageContent,
      pageNumber: 6 + index,
      isMultiPage: true,
      isChapter: false
    })),
    
    // 造化人 - 寄大北
    {
      id: 9,
      title: "造化人",
      content: "寄大北",
      pageNumber: 9,
      isChapter: true
    },
    ...splitContentIntoPages(`别有乾坤造化人，霜花春草即相逢。
夏虫冰语寻常看，摘得鲜莓趁北风。

感谢学校，能让我更加丰富，再次感谢。学校里没什么特别的，就松鼠很多。同时，因为靠近郊外，平时还经常看到各种鸟在上空徘徊，我怀疑甚至还有鹰。校园里的猫也不少，胖，慵懒且不怕人，每次出去上课的时候总看到他们慵懒的晒着太阳，于是时常感慨，人不如猫，哈哈哈。此外，遍植松柏，野花到暮秋仍然五彩缤纷，不得不说，很符合学校气质。好友至，遂停笔，光明正大搁置。

（2021年九月二十日） 题目：造化人- 寄大北`, 12).map((pageContent, index) => ({
      id: 10 + index,
      title: "造化人 - 寄大北",
      content: pageContent,
      pageNumber: 10 + index,
      isMultiPage: true,
      isChapter: false
    })),
    
    // 风 - 白衣客
    {
      id: 12,
      title: "风",
      content: `大风起，飘然若隔世，于是顿起心思，也狂妄一番，故写下此词，
"白衣客"。3月4日。

我有清霜一剑，剑出九州冬寒。冷光侵肌骨，飞雪连天漱。

且喜且悲且行住，无恃无恐无忧怒。

少年白衣成，素服净履去。

策马乘云漫行处，浅梦星河江山度。`,
      pageNumber: 12,
      isChapter: false
    },
    
    // 泷月：步履不停
    {
      id: 13,
      title: "月三则",
      content: "其一：泷月 步履不停",
      pageNumber: 13,
      isChapter: true
    },
    ...splitContentIntoPages(`大概一个人在深宵中站的久了，连露水里都是狭隘和迷茫。有时候常会想起从前的自己，那个走路带风，眼角眉梢尽是潇洒的孩子，她的胆量比我大，相信的东西比我多。

然而生活被驳杂的事物充斥着，我几乎已忘记了曾经那些坚守的心愿，直到后来的那几次骑行，几十公里的路程，看了浑河和青山，骑行在大大小小的城市，当以一种近乎客观的距离，去观察那些截然不同的风物时，才蓦然发现，自己活的其实多么渺小。"你是生活了6570多天，还是仅生活了一天，却重复了6570多次"我开始扪心自问。你根本就没有规划，你每天都在学什么？

其实人生并不是要功成名就才算圆满，幸福也并非你得到了什么东西，而是你能给予这个世界多少，于我而言，惟愿有天能成为个渊博而有趣的人，既心怀浪漫宇宙，也关心衣食住行，爱所爱之人，爱所喜之事并为之坚持下去。当然，我没有天赋，但我很想试试一个普通人心怀梦想会是什么样子。毕竟我才18，我的未来还是未知的。但我真的害怕我会成为我现在看不上的人。

愿有一天，也能在清简的生活中，散发出绚烂的光，无论高贵或平凡。愿能在细碎冗长的日子里，依旧倔强的，还能不肯向命运折戟。

2021年7月15日秉笔`, 12).map((pageContent, index) => ({
      id: 14 + index,
      title: "其一：泷月 步履不停",
      content: pageContent,
      pageNumber: 14 + index,
      isMultiPage: true,
      isChapter: false
    })),

    // 明月：烟火人间
    {
      id: 18,
      title: "月三则",
      content: "其二：明月 烟火人间",
      pageNumber: 18,
      isChapter: true
    },
    ...splitContentIntoPages(`自从发现了三个月亮的秘密后，每晚临睡前，我都会掀开窗帘，静静的望上一会儿，月色温柔，先是洒在床上，继而缓缓蔓延，终于涌上心头，卷起一层层过往的波澜，在记忆中此起彼伏。而眼前一帧帧如记录片的画面里，浮现最多的，总是儿时生长的那个四线小城。营口不大，小时候一直都是妈妈爸爸开车带我去各种地方，虽然知道各种路线但从没自己走过。六月份的时候，我回去过一次，在路上自行车，从恒大绿洲到老兴隆，再从辽河公园到得胜市场的转上一圈，算来也不过才一小时而已。可就是这样朴实甚至有些破旧日的小城，每每忆起，总仿佛罩了一层怀旧的滤镜般，让人格外温暖。如果非要用一个词形容这种感觉的话。我想说，那是人间烟火。那烟火气是儿时放学的路上，晚霞中飘着不知谁家炖肉的香味儿，好像一直往前走就能走进温柔的夕阳里；那烟火气是街边女人街嘈杂的叫卖声，有人在开怀纵酒撸串，也有人被音响里的放的歌击中心事，醉醺醺的从小吃摊站起，转身钻进夏日的晚风里；那烟火气中没有半点绚目的光彩，只有细水长流般的淡然和街长里短的琐细繁杂。

可这就是这朴实而又真切的日子，却饱含着普通人对生活最纯真的希翼，这平凡的人间烟火，已成为我最隆重的回忆和直戳心底的感动。每当想起那些欢乐的时光，心中总会涌起一团暖意，抵御着水瓶的自卑和恐惧，我始终相信，那些历经无数，尝遍人间烟火气的人，灵魂会更加生动而干净，无论世态炎凉，依然心向星光。月若灵犀知我意，清辉夜夜化丹心。此刻，我将丹心写在这一窗清月里，亦不知能否尽述其详，可能文字也不过是一个承载的容器，很多情感连身心都难以尽载，何況这外化之物。而我，只想记录下这滚滚红尘中，所有的赏心悦目，寒来暑往。惟愿此生能永远清澈明澈。渴望独立，又害怕孤独…..

2021年六月二十一日秉笔 题目：月两则`, 12).map((pageContent, index) => ({
      id: 19 + index,
      title: "其二：明月 烟火人间",
      content: pageContent,
      pageNumber: 19 + index,
      isMultiPage: true,
      isChapter: false
    })),
    
    // 其三 - 深夜骑行
    {
      id: 23,
      title: "月三则",
      content: "其三：深夜骑行",
      pageNumber: 23,
      isChapter: true
    },
    ...splitContentIntoPages(`对我而言，此行或许并无意义
借着晚间的风与月，满腔诗兴涌起
放生，欲融入此间虫鸣
一阵静默
月呢，或许月也不愿听
不过它默默陪伴此行
指引归路的人，亦足矣
惆怅归来有月知`, 8).map((pageContent, index) => ({
      id: 24 + index,
      title: "其三：深夜骑行",
      content: pageContent,
      pageNumber: 24 + index,
      isMultiPage: true,
      isChapter: false
    })),
    
    // 其一 - 大雪：日常思"雪"记
    {
      id: 26,
      title: "雪二则",
      content: "其一：大雪：日常思\"雪\"记",
      pageNumber: 26,
      isChapter: true
    },
    ...splitContentIntoPages(`大雪，予未来的雪和未见的你。
雪没来的日子里，
想你了，
想你穿白裙的身影。
伏在书桌上的时候，炉火噼啪地燃着，
脚边的羊毛毯显得静穆又温暖。
慢慢翻着亚平宁的诗集。
我的仙子，是否也曾在天宫里、倾倒过泉水。
知道你不喜欢牡丹，
见面的时候，
定会送你一个、摘自春天的玫瑰。
窗前的月光徐徐地垂下，索性吹灭了烛台，起身望着篱墙外的小道。
一只白鹿轻巧地穿过了它，又隐没林间。
只剩下雪山顶上星子的光。
北冰洋的气息呵，何时能被西风送到身前。
十二月未来的offer啊，你何时到呢？何时到呢？`, 12).map((pageContent, index) => ({
      id: 27 + index,
      title: "其一：大雪：日常思\"雪\"记",
      content: pageContent,
      pageNumber: 27 + index,
      isMultiPage: true,
      isChapter: false
    })),

    // 其二 - 东方隐士
    {
      id: 31,
      title: "雪二则 其二",
      content: "东方隐士",
      pageNumber: 31,
      isChapter: true
    },
    ...splitContentIntoPages(`大雪后的日子里，
山中，雪依然没有到来。
坐在砖砌得火炉畔，信手倒一杯热腾腾的山茶。
手里的诗集沐浴着刚起的光。
披上我那威风黑色的风衣，
漫不经心地将脚伸入皮鞋。
拾起太阳味道的手杖，已在想着，
不妨去山顶看看，
来寄一封往北美的信。
门前的小路已被针叶堆满，
静谧得剩下喜雀的鸣叫。
老却的松树顺势躺在溪边，
一步跨过，趁天未冷，
伸手拾得一块十二万年的卵石。
且将它作为、冬日里的邮费。
蜿蜿蜒蜒的小路，
就如起起伏伏的心思。
幸好大多是上坡。
便可鼓起勇气，
大声向身侧的金翅大鹏问一句，
"雪什么时候来"。
它往上飞走了。

2022年一月十日秉笔

注：其一于其二中的雪不仅指雪，亦指我的offer，此处两种理解并存，正是余故意为之。`, 12).map((pageContent, index) => ({
      id: 32 + index,
      title: "东方隐士",
      content: pageContent,
      pageNumber: 32 + index,
      isMultiPage: true,
      isChapter: false
    })),
    
    // 孤独谈系列
    // 初谈
    {
      id: 36,
      title: "孤独闲谈——隙间随笔",
      content: "初谈",
      pageNumber: 36,
      isChapter: true
    },
    ...splitContentIntoPages(`初谈
        孤独不是人生的选择，而是必经之路。只是有些人走时间长，有些人走的时间短罢了。行人们有的平和、有的释怀、有的焦虑，毕竟，孤独有三种境界，独行、独处、独立。（2021年十月三日秉笔）  
2022年4月9日`, 15).map((pageContent, index) => ({
      id: 37 + index,
      title: "初谈",
      content: pageContent,
      pageNumber: 37 + index,
      isMultiPage: true,
      isChapter: false
    })),
    
    // 二谈心理学上的孤独
    {
      id: 42,
      title: "孤独谈",
      content: "二谈心理学上的孤独",
      pageNumber: 42,
      isChapter: true
    },
    ...splitContentIntoPages(`二谈心理学上的孤独

独处起源于人性中"疏远人"心态，即逃避自己不想面对的，躲避可能发生的冲突。终于内心矛盾的和解，自身人格的强大。  独处有三种境界：在人群中向往独处，习惯独处，在人群中照样保持独处状态。  在独处时，因为会直面内心冲突，会引发恐惧，迷茫痛苦。我们倾向保持内心和谐，害怕触及这些矛盾，因此在独处中可能会有：一，与平时相反的行为；二，构造脑补自己的理想人格（在艺术型人格中极常见，我自己就是）；三，批判一切价值，否认自己。为了减少这些（解决不大可能），我们必须去努力了解自己的感受和需求，这也是独处所赋予的意义：审视内心，面对平时看不到的自己。慢慢接近冲突，努力直面，承受不是自己内心需要但外界加塞的与自身价值关的冲击，于是慢慢寻找到解决之策，内心慢慢获得更多自由，乃至达到群居时亦是自身独处。（2021年10月25号秉笔）`, 15).map((pageContent, index) => ({
      id: 43 + index,
      title: "二谈心理学上的孤独",
      content: pageContent,
      pageNumber: 43 + index,
      isMultiPage: true,
      isChapter: false
    })),
    
    // 三谈大学的孤独
    {
      id: 48,
      title: "孤独谈",
      content: "三谈大学的孤独",
      pageNumber: 48,
      isChapter: true
    },
    ...splitContentIntoPages(`三谈大学的孤独

今日下午，无事听了南农教授王银泉讲座，谈中外文化交流，大师之言，披靡思考。余大有感悟，方悟大学之本质，先前之肤浅。以身体之自由而评大学，不知道我未来的学校与普通大学区别到底有多大，甚至我连未来学校在哪还无从得知，但就今日所感来看，区别可真是太大了。身体之自由非精神之自由，精神不自由，难以独立，不独立，难以真正孤独。大部分人之自由，所谓身体自由，大部分人之孤独，所谓身体之孤独。孤独的享受，需要精神的明亮，独立的思考。在中国，真正大学之少，真正大学生之少，可惜。   乃如蔡元培先生所说，大学不为谋官，不为求财，而为求思想。大学并非有大厦，有大师，而思想之碰撞争鸣，在精神之独立明亮。  谈"以我为主"，说得是中外文化，难道不可用于自身？若无原则性错误，以我为主有何错误？既然有文化之自信，为何不可有思想之自信？以我为主，求诸自身，自信思考。岂可随便因他人想法而转变立场？百家争鸣而百家异声，百家异声而从同道，此所谓思想之独立，精神之明亮！是谓思想之自由，是谓真孤独也！  谓之，管他熙熙攘攘阳关道，我偏要一条独木桥走到黑。                                                             
（2021年11月19日秉笔）`, 15).map((pageContent, index) => ({
      id: 49 + index,
      title: "三谈大学的孤独",
      content: pageContent,
      pageNumber: 49 + index,
      isMultiPage: true,
      isChapter: false
    })),
    
    // 孤独四谈
    {
      id: 54,
      title: "孤独谈",
      content: "孤独四谈",
      pageNumber: 54,
      isChapter: true
    },
    ...splitContentIntoPages(`孤独四谈：

短期（瞬间）的满足具有大众性，即时性，短暂性。长期满足就有独一性，丰裕性，延迟性。因此真正的孤独者，追求得应该是长期满足。  我们总是被各式各样的文化，传统，大众舆论，社会压力，权威等等给予无数限制，然而最终把限制套在我们身上的，是我们自己。很多时候，我们习惯于枷锁的重量，乃至于忽视它；亦或是知道但不想、不敢摘下枷锁。比较这些厚重的枷锁，似乎给予我们一些活在世上的安全感—因为几乎所有人都心安理得地戴着枷锁。也况且自幼就戴着枷锁，这么久，怎么自由地走也忘了。或许，适应会很难，但自由总是好的。读《一个人就一个人》有感。（2021年十二月七日秉笔）`, 15).map((pageContent, index) => ({
      id: 55 + index,
      title: "孤独四谈",
      content: pageContent,
      pageNumber: 55 + index,
      isMultiPage: true,
      isChapter: false
    })),
    
    // 孤独五谈
    {
      id: 60,
      title: "孤独谈",
      content: "孤独五谈",
      pageNumber: 60,
      isChapter: true
    },
    ...splitContentIntoPages(`孤独五谈

我觉得孤独很正常，佛家的修行不就在于出世清修吗。 现在有很多人都在追求精神上的独立思想上的独立，但他们并不能像大师一样，达到真正的思想自由还不甘心自甘堕落，于是他们就处于一种半颓废的状态。但这其实也很正常，并不是所有人都会真正的达到思想上的独立，山水蒙卦中说过，山下有险，险而止，蒙。所有人都会在某一个阶段，觉得特别的迷茫，不知道该从何下手，梦想，总是离自己特别遥远。卢梭说过一句话，痛苦来源于能力与欲望之间的不平衡，所以在迷茫的时候就做好自己该做的事情，努力的充实自己。终究会找到自己的方向的，
与其把孤独浪漫化，不如把它制度化。所谓制度化，就是把“情绪化的独处”变成“带产出的独处”：固定的读写时间、稳定的体能训练、可量化的小目标。王阳明讲“慎独”，不是让你在夜里感伤，而是在无人处仍旧自检、自持。真正的独立不是一种姿态，而是一套在低谷期仍能执行的流程。

其次，别把孤独当成对世界的撤退。更好的理解是：暂时撤回港湾，以便校准航向。人和世界的关系像潮汐，涨落有时。你收回注意力，是为了把注意力还给更值得的人和事。所谓“断舍离”，不是一刀切，而是把噪音降到阈值以下，让信号浮起来。`, 15).map((pageContent, index) => ({
      id: 61 + index,
      title: "孤独五谈",
      content: pageContent,
      pageNumber: 61 + index,
      isMultiPage: true,
      isChapter: false
    })),
    
    // 孤独六谈
    {
      id: 66,
      title: "孤独谈",
      content: "孤独六谈",
      pageNumber: 66,
      isChapter: true
    },
    ...splitContentIntoPages(`孤独六谈
      孤独这几谈的本质，大抵是出于少年人的义愤和对自己的一些不满，除了一谈，所说的孤独似乎总带着一种涌动的愤怒，亦或是冰冷的嘲弄。其实想来，孤独应该是一种温暖的人文境界，若要形象些，物像之中，应该是风和雨中的青灯。  遗世风中，举灯前行，若是，无愧此生。  
      今日读完《世界尽头的咖啡馆》，《第一人称单数》。 
      （2021年十二月二十八日秉笔）  
`, 15).map((pageContent, index) => ({
      id: 67 + index,
      title: "孤独六谈",
      content: pageContent,
      pageNumber: 67 + index,
      isMultiPage: true,
      isChapter: false
    })),
    
    // 浣溪沙-次韵
    {
      id: 72,
      title: "浣溪沙",
      content: `次韵

欲问浮生几度秋？无人会，上层楼。
已知前路转悠悠，孤注一掷何时休？
酒阑人散我独留，对影残念与君酬。

2022年5月13日`,
      pageNumber: 72,
      isChapter: false
    },
    
    // 反思
    {
      id: 73,
      title: "反思",
      content: "反思",
      pageNumber: 73,
      isChapter: true
    },
    ...splitContentIntoPages(`反思

最近总在反思，究其原因。一是自己想写的东西很多时候写不出来，思想太狭隘。
二，对最近写得东西不大满意，有几首情感不是太真，套版而制。
三，回顾以往诗词，当时虽然语言可能不通，但却情真意切，字里行间流露的，是一种纯粹。而今，虽然在文辞上小有进步，但写得东西情感真挚却少了。这不行。
四，写什么，要看对诗词的理解和认识了。每个人境遇不同，性情不同，看事物的角度也不同。愚弄字，不喜拘泥一式，（也有水平太低，看书太少太杂的原因）往往喜欢去求新。
希望能在这阡陌交通中留下更多吧……
愚生拜
附上几首小诗，供诸君聊作一笑

浣溪沙（分韵得"迎"字）

夕照西山一般醒。泼如霞壁子娉婷。风回渐渐渐无形。时剥橘黄分十瓣，偶听松绿涨千声。窗间云鹤往来迎。

五绝 修心

贪心无止境，寡欲一身轻。

修炼知天命，余生千虑清。

七绝 冷

冬寒彻骨无好梦，冷雪喧嚣独自听。

消受楼中四环顾，梅窗归坐无能凭。

独

琴瑟何必终成对？

总可远眺，总可意会；

至交无需酩酊醉，

谦谦如影，淡淡如水。

本欲长歌力无足，

风往北吹，燕往南飞；

人去梦残他日归，

白云悠悠，衣袖挥挥。`, 15).map((pageContent, index) => ({
      id: 74 + index,
      title: "反思",
      content: pageContent,
      pageNumber: 74 + index,
      isMultiPage: true,
      isChapter: false
    }))
  ]
};

interface BookReaderProps {
  isOpen: boolean;
  onClose: () => void;
}

const BookReader: React.FC<BookReaderProps> = ({ isOpen, onClose }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [fontSize, setFontSize] = useState(16);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [showTableOfContents, setShowTableOfContents] = useState(false);
  const bookRef = useRef<HTMLDivElement>(null);

  // 确保 currentPage 在有效范围内
  useEffect(() => {
    if (currentPage >= bookData.pages.length) {
      setCurrentPage(Math.max(0, bookData.pages.length - 2));
    }
  }, [currentPage]);

  // 翻页动画 - 双页模式，每次翻两页
  const handlePageTurn = useCallback((direction: 'next' | 'prev') => {
    if (isFlipping) return;
    
    setIsFlipping(true);
    
    setTimeout(() => {
      if (direction === 'next' && currentPage < bookData.pages.length - 2) {
        setCurrentPage(currentPage + 2);
      } else if (direction === 'prev' && currentPage > 0) {
        setCurrentPage(currentPage - 2);
      }
      setIsFlipping(false);
    }, 300);
  }, [currentPage, isFlipping]);

  // 键盘事件
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          handlePageTurn('prev');
          break;
        case 'ArrowRight':
          handlePageTurn('next');
          break;
        case 'Escape':
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, handlePageTurn, onClose]);

  // 计算阅读进度 - 双页模式
  const progress = ((currentPage + 2) / bookData.pages.length) * 100;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-[#2C2721] text-[#F1EFE0] overflow-hidden"
      >
        {/* 顶部导航栏 */}
        <nav className="absolute top-0 left-0 right-0 z-10 bg-black/20 backdrop-blur-sm border-b border-gray-600/20">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={onClose}
                className="flex items-center gap-2 text-[#F1EFE0] hover:text-gray-300 transition-colors"
              >
                <Home className="w-5 h-5" />
                <span>Back</span>
              </button>
              <div className="h-6 w-px bg-gray-600/30"></div>
              <h1 className="text-xl font-bold" style={{fontFamily: 'Vixa-Regular, sans-serif'}}>
                {bookData.title}
              </h1>
            </div>
            
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowTableOfContents(!showTableOfContents)}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <List className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </nav>

        {/* 主阅读区域 - 双页A5布局 */}
        <div className="flex h-full pt-20">
          {/* 双页容器 */}
          <div className="flex-1 flex justify-center items-center p-8">
            <div className="flex gap-4">
              {/* 左页 */}
              <motion.div
                ref={bookRef}
                className="relative bg-[#2C2721] shadow-2xl overflow-hidden"
                style={{
                  width: '148mm',
                  height: '210mm',
                  maxWidth: '148mm',
                  maxHeight: '210mm',
                  aspectRatio: '148/210'
                }}
              >
                {/* 左页内容 */}
                {bookData.pages[currentPage] && bookData.pages[currentPage].isChapter ? (
                  // 章节页面 - A5特殊布局
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentPage}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="relative w-full h-full"
                      style={{
                        paddingTop: '36mm',    // 上边距
                        paddingBottom: '28mm', // 下边距
                        paddingLeft: '28mm',  // 左边距
                        paddingRight: '36mm'   // 右边距
                      }}
                    >
                      {/* 章节标识模块 - 右下象限 */}
                      <div className="absolute bottom-0 right-0 flex items-end"
                           style={{
                             bottom: '28mm',
                             right: '36mm'
                           }}>
                        {/* 章节标题 */}
                        <div 
                          className="text-right"
                          style={{
                            fontFamily: 'system-ui, -apple-system, sans-serif',
                            fontSize: '18pt',
                            fontWeight: '500',
                            color: '#F1EFE0',
                            lineHeight: '1'
                          }}
                        >
                          {bookData.pages[currentPage]?.title}
                        </div>
                        
                        {/* 竖线 */}
                        <div 
                          className="ml-2"
                          style={{
                            width: '1.5pt',
                            height: '36pt',
                            backgroundColor: '#F1EFE0'
                          }}
                        ></div>
                        
                        {/* 页码 */}
                        <div 
                          className="ml-1"
                          style={{
                            fontFamily: 'system-ui, -apple-system, sans-serif',
                            fontSize: '14pt',
                            fontWeight: '400',
                            color: '#F1EFE0',
                            lineHeight: '1'
                          }}
                        >
                          {bookData.pages[currentPage]?.pageNumber}
                        </div>
                      </div>

                      {/* 页面内容 - 中下部水平居中 */}
                      <div 
                        className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center"
                        style={{
                          width: 'calc(100% - 64mm)', // 减去左右边距
                          top: '65%' // 使内容落在页面高度60%-70%处
                        }}
                      >
                        <div 
                          style={{
                            fontFamily: 'serif',
                            fontSize: '12.5pt',
                            fontWeight: '400',
                            color: '#F1EFE0',
                            lineHeight: '1.6',
                            textAlign: 'center'
                          }}
                        >
                          {bookData.pages[currentPage]?.content.split('\n').map((line, index) => (
                            <div key={index} className="mb-2">
                              {line}
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                ) : (
                  // 文章页面 - 正常文章布局
                  <div className="absolute inset-0 p-12">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={currentPage}
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 50 }}
                        transition={{ duration: 0.3 }}
                        className="h-full flex flex-col"
                      >
                        {/* 页面标题 */}
                        <div className="text-center mb-8">
                          <h2 className="text-3xl font-bold mb-2" style={{fontFamily: 'Vixa-Regular, sans-serif'}}>
                            {bookData.pages[currentPage]?.title}
                          </h2>
                          <p className="text-gray-400">第 {bookData.pages[currentPage]?.pageNumber} 页</p>
                        </div>
                        
                        {/* 页面内容 */}
                        <div 
                          className="flex-1 text-center leading-relaxed"
                          style={{ 
                            fontSize: `${fontSize}px`,
                            fontFamily: 'Vixa-Regular, sans-serif',
                            lineHeight: '1.8'
                          }}
                        >
                          {bookData.pages[currentPage]?.content.split('\n').map((paragraph, index) => (
                            <p key={index} className="mb-4 text-[#F1EFE0]">
                              {paragraph}
                            </p>
                          ))}
                        </div>
                        
                        {/* 页面底部 */}
                        <div className="mt-8 pt-4 border-t border-gray-600/20">
                          <div className="flex justify-between items-center text-sm text-gray-400">
                            <span>{bookData.author}</span>
                            <span>{currentPage + 1} / {bookData.pages.length}</span>
                          </div>
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                )}
              </motion.div>

              {/* 右页 */}
              <motion.div
                className="relative bg-[#2C2721] shadow-2xl overflow-hidden"
                style={{
                  width: '148mm',
                  height: '210mm',
                  maxWidth: '148mm',
                  maxHeight: '210mm',
                  aspectRatio: '148/210'
                }}
              >
                {/* 右页内容 - 显示下一页或空白 */}
                {currentPage + 1 < bookData.pages.length ? (
                  <div className="absolute inset-0 p-12">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={currentPage + 1}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ duration: 0.3 }}
                        className="h-full flex flex-col"
                      >
                        {/* 页面标题 */}
                        <div className="text-center mb-8">
                          <h2 className="text-3xl font-bold mb-2" style={{fontFamily: 'Vixa-Regular, sans-serif'}}>
                            {bookData.pages[currentPage + 1]?.title}
                          </h2>
                          <p className="text-gray-400">第 {bookData.pages[currentPage + 1]?.pageNumber} 页</p>
                        </div>
                        
                        {/* 页面内容 */}
                        <div 
                          className="flex-1 text-center leading-relaxed"
                          style={{ 
                            fontSize: `${fontSize}px`,
                            fontFamily: 'Vixa-Regular, sans-serif',
                            lineHeight: '1.8'
                          }}
                        >
                          {bookData.pages[currentPage + 1]?.content.split('\n').map((paragraph, index) => (
                            <p key={index} className="mb-4 text-[#F1EFE0]">
                              {paragraph}
                            </p>
                          ))}
                        </div>
                        
                        {/* 页面底部 */}
                        <div className="mt-8 pt-4 border-t border-gray-600/20">
                          <div className="flex justify-between items-center text-sm text-gray-400">
                            <span>{bookData.author}</span>
                            <span>{currentPage + 2} / {bookData.pages.length}</span>
                          </div>
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                ) : (
                  // 空白右页
                  <div className="w-full h-full bg-[#2C2721]"></div>
                )}
              </motion.div>
            </div>
            
            {/* 翻页按钮 */}
            <button
              onClick={() => handlePageTurn('prev')}
              disabled={currentPage === 0 || isFlipping}
              className="absolute left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center z-10"
            >
              <ChevronLeft className="w-6 h-6 text-[#F1EFE0]" />
            </button>
            
            <button
              onClick={() => handlePageTurn('next')}
              disabled={currentPage >= bookData.pages.length - 2 || isFlipping}
              className="absolute right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center z-10"
            >
              <ChevronRight className="w-6 h-6 text-[#F1EFE0]" />
            </button>
          </div>
        </div>

        {/* 底部进度条 */}
        <div className="absolute bottom-0 left-0 right-0 bg-black/20 backdrop-blur-sm border-t border-gray-600/20 p-4">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            <div className="flex items-center gap-4">
              <BookOpen className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-400">
                阅读进度: {Math.round(progress)}%
              </span>
            </div>
            
            <div className="flex-1 mx-8">
              <div className="w-full bg-gray-600/30 rounded-full h-2">
                <motion.div
                  className="bg-[#F1EFE0] h-2 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400">
                {currentPage + 1}-{Math.min(currentPage + 2, bookData.pages.length)} / {bookData.pages.length}
              </span>
            </div>
          </div>
        </div>

        {/* 设置面板 */}
        <AnimatePresence>
          {showSettings && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="absolute top-20 right-6 w-80 bg-black/80 backdrop-blur-md rounded-xl border border-gray-600/20 p-6"
            >
              <h3 className="text-lg font-bold mb-4" style={{fontFamily: 'Vixa-Regular, sans-serif'}}>
                阅读设置
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">字体大小</label>
                  <input
                    type="range"
                    min="12"
                    max="24"
                    value={fontSize}
                    onChange={(e) => setFontSize(Number(e.target.value))}
                    className="w-full"
                  />
                  <div className="text-xs text-gray-500 mt-1">{fontSize}px</div>
                </div>
                
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isDarkMode}
                      onChange={(e) => setIsDarkMode(e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm">深色模式</span>
                  </label>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 目录面板 */}
        <AnimatePresence>
          {showTableOfContents && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="absolute top-20 left-6 w-80 bg-black/80 backdrop-blur-md rounded-xl border border-gray-600/20 p-6 max-h-[70vh] flex flex-col"
            >
              <h3 className="text-lg font-bold mb-4" style={{fontFamily: 'Vixa-Regular, sans-serif'}}>
                目录
              </h3>
              
              <div className="space-y-2 overflow-y-auto flex-1 pr-2 book-toc-scroll" style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#000000 transparent'
              }}>
                {bookData.pages.map((page, index) => (
                  <button
                    key={page.id}
                    onClick={() => setCurrentPage(index)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      currentPage === index 
                        ? 'bg-[#F1EFE0]/20 text-[#F1EFE0]' 
                        : 'hover:bg-white/10 text-gray-400'
                    }`}
                  >
                    <div className="font-medium">{page.title}</div>
                    <div className="text-sm opacity-75">
                      第 {page.pageNumber} 页
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

export default BookReader;
